import React, { useState, useEffect, useCallback } from 'react';
import { 
  ShoppingCart, 
  CreditCard, 
  Package,
  Download,
  Search,
  Filter,
  RefreshCw,
  DollarSign,
  TrendingUp,
  Calendar,
  ChevronDown
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { dataService, Order, Payment, InventoryTransaction, MenuItem, RawMaterial } from '../../services/dataService';

interface ProfitCalculation {
  menuItemId: string;
  menuItemName: string;
  quantitySold: number;
  totalRevenue: number;
  totalCost: number;
  profit: number;
  profitMargin: number;
  ingredients: {
    rawMaterialId: string;
    rawMaterialName: string;
    quantityUsed: number;
    unitCost: number;
    totalCost: number;
  }[];
}

interface TodayReportData {
  orders: Order[];
  payments: Payment[];
  stockChanges: InventoryTransaction[];
  menuItems: MenuItem[];
  rawMaterials: RawMaterial[];
  profitCalculations: ProfitCalculation[];
  summary: {
    totalOrders: number;
    totalRevenue: number;
    totalTransactions: number;
    stockChanges: number;
    totalProfit: number;
    totalCost: number;
    averageProfitMargin: number;
  };
}

const TodayReport: React.FC = () => {
  const { tenantData } = useAuth();
  const [activeTab, setActiveTab] = useState<'orders' | 'transactions' | 'stocks' | 'profit'>('orders');
  
  // Date filter state
  type DateFilterType = 'today' | 'this-week' | 'this-month' | 'custom' | 'range' | 'all-time';
  const [dateFilterType, setDateFilterType] = useState<DateFilterType>('today');
  const [customDate, setCustomDate] = useState(new Date().toISOString().split('T')[0]);
  const [rangeStartDate, setRangeStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [rangeEndDate, setRangeEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [showDatePickers, setShowDatePickers] = useState(false);
  
  const [reportData, setReportData] = useState<TodayReportData>({
    orders: [],
    payments: [],
    stockChanges: [],
    menuItems: [],
    rawMaterials: [],
    profitCalculations: [],
    summary: {
      totalOrders: 0,
      totalRevenue: 0,
      totalTransactions: 0,
      stockChanges: 0,
      totalProfit: 0,
      totalCost: 0,
      averageProfitMargin: 0
    }
  });
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Filtering state
  const [orderStatusFilter, setOrderStatusFilter] = useState<'all' | 'active' | 'completed' | 'cancelled'>('all');
  const [paymentMethodFilter, setPaymentMethodFilter] = useState<'all' | 'cash' | 'card' | 'digital'>('all');
  const [transactionTypeFilter, setTransactionTypeFilter] = useState<'all' | 'in' | 'out'>('all');
  const [stockViewMode, setStockViewMode] = useState<'summary' | 'detailed'>('summary');
  const [profitFilter, setProfitFilter] = useState<'all' | 'rawMaterials'>('all');

  // Helper function to safely convert date
  const safeToDate = (date: any): Date => {
    if (!date) return new Date();
    if (date instanceof Date) return date;
    if (date.toDate && typeof date.toDate === 'function') return date.toDate();
    if (typeof date === 'string' || typeof date === 'number') return new Date(date);
    return new Date();
  };

  // Get date range based on filter type
  const getDateRangeForFilter = useCallback((filterType: DateFilterType): { start: Date; end: Date; label: string } => {
    const now = new Date();
    let start: Date;
    let end: Date;
    let label: string;

    switch (filterType) {
      case 'today':
        start = new Date(now);
        start.setHours(0, 0, 0, 0);
        end = new Date(now);
        end.setHours(23, 59, 59, 999);
        label = "Today's Report";
        break;

      case 'this-week':
        // Get current week (Monday to Sunday)
        start = new Date(now);
        const dayOfWeek = start.getDay();
        const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Sunday = 0, Monday = 1
        start.setDate(start.getDate() - daysToMonday);
        start.setHours(0, 0, 0, 0);
        
        end = new Date(start);
        end.setDate(end.getDate() + 6); // Add 6 days to get Sunday
        end.setHours(23, 59, 59, 999);
        label = "This Week's Report";
        break;

      case 'this-month':
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        start.setHours(0, 0, 0, 0);
        
        end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        end.setHours(23, 59, 59, 999);
        label = "This Month's Report";
        break;

      case 'custom':
        start = new Date(customDate);
        start.setHours(0, 0, 0, 0);
        end = new Date(customDate);
        end.setHours(23, 59, 59, 999);
        label = `Report for ${new Date(customDate).toLocaleDateString()}`;
        break;

      case 'range':
        start = new Date(rangeStartDate);
        start.setHours(0, 0, 0, 0);
        end = new Date(rangeEndDate);
        end.setHours(23, 59, 59, 999);
        label = `Report: ${new Date(rangeStartDate).toLocaleDateString()} - ${new Date(rangeEndDate).toLocaleDateString()}`;
        break;

      case 'all-time':
        // Set to a very early date (business likely started after 2020)
        start = new Date(2020, 0, 1);
        start.setHours(0, 0, 0, 0);
        end = new Date(now);
        end.setHours(23, 59, 59, 999);
        label = "All Time Report";
        break;

      default:
        start = new Date(now);
        start.setHours(0, 0, 0, 0);
        end = new Date(now);
        end.setHours(23, 59, 59, 999);
        label = "Today's Report";
    }

    return { start, end, label };
  }, [customDate, rangeStartDate, rangeEndDate]);

  // Helper function to format time
  const formatTime = (date: any): string => {
    try {
      const dateObj = safeToDate(date);
      return dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch (error) {
      return 'Unknown time';
    }
  };

  const loadReportData = useCallback(async () => {
    if (!tenantData?.id) return;

    try {
      setLoading(true);
      
      // Get date range based on current filter
      const { start, end } = getDateRangeForFilter(dateFilterType);
      
      // Load all orders, payments, and transactions
      const [allOrders, allPayments, allTransactions, menuItems, rawMaterials] = await Promise.all([
        dataService.getOrders(tenantData.id),
        dataService.getPayments(tenantData.id),
        dataService.getInventoryTransactions(tenantData.id),
        dataService.getMenuItems(tenantData.id),
        dataService.getRawMaterials(tenantData.id)
      ]);

      // Filter data by date range
      const filteredOrders = allOrders.filter(order => {
        if (!order.createdAt) return false;
        const orderDate = new Date(order.createdAt);
        return orderDate >= start && orderDate <= end;
      });

      const filteredPayments = allPayments.filter(payment => {
        if (!payment.createdAt) return false;
        const paymentDate = new Date(payment.createdAt);
        return paymentDate >= start && paymentDate <= end;
      });

      const filteredTransactions = allTransactions.filter(tx => {
        if (!tx.createdAt) return false;
        const txDate = new Date(tx.createdAt);
        return txDate >= start && txDate <= end;
      });

      // Calculate profit for filtered orders
      const profitCalculations = calculateProfitForOrders(filteredOrders, menuItems, rawMaterials);
      
      // Calculate summary
      const totalRevenue = filteredPayments.reduce((sum: number, payment: Payment) => sum + (payment.amount || 0), 0);
      const totalProfit = profitCalculations.reduce((sum: number, calc: ProfitCalculation) => sum + calc.profit, 0);
      const totalCost = profitCalculations.reduce((sum: number, calc: ProfitCalculation) => sum + calc.totalCost, 0);
      const averageProfitMargin = totalRevenue > 0 ? (totalProfit / totalRevenue) * 100 : 0;

      setReportData({
        orders: filteredOrders,
        payments: filteredPayments,
        stockChanges: filteredTransactions,
        menuItems,
        rawMaterials,
        profitCalculations,
        summary: {
          totalOrders: filteredOrders.length,
          totalRevenue,
          totalTransactions: filteredPayments.length,
          stockChanges: filteredTransactions.length,
          totalProfit,
          totalCost,
          averageProfitMargin
        }
      });
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Error loading report data:', error);
    } finally {
      setLoading(false);
    }
  }, [tenantData, dateFilterType, getDateRangeForFilter]);

  useEffect(() => {
    loadReportData();
  }, [loadReportData]);

  const getStatusColor = (status: string | undefined | null) => {
    if (!status || typeof status !== 'string') {
      return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    }
    switch (status.toLowerCase()) {
      case 'completed':
      case 'active':
        return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-200';
      case 'cancelled':
      case 'failed':
        return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    }
  };

  const getPaymentMethodColor = (method: string | undefined | null) => {
    if (!method) {
      return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    }
    switch (method.toLowerCase()) {
      case 'cash':
        return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-200';
      case 'card':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-200';
      case 'digital':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    }
  };

  // Calculate profit for orders
  const calculateProfitForOrders = (orders: Order[], menuItems: MenuItem[], rawMaterials: RawMaterial[]): ProfitCalculation[] => {
    const profitMap = new Map<string, ProfitCalculation>();

    orders.forEach(order => {
      order.items?.forEach(orderItem => {
        const menuItem = menuItems.find(item => item.id === orderItem.menuItemId);
        if (!menuItem) return;

        const existing = profitMap.get(orderItem.menuItemId);
        const revenue = orderItem.totalPrice;
        
        // Calculate ingredient costs
        const ingredients = menuItem.ingredients?.map(ingredient => {
          const rawMaterial = rawMaterials.find(rm => rm.id === ingredient.rawMaterialId);
          if (!rawMaterial) return null;

          const unitCost = rawMaterial.costPerSubUnit || rawMaterial.costPerUnit || 0;
          const totalCost = ingredient.quantity * unitCost;

          return {
            rawMaterialId: ingredient.rawMaterialId,
            rawMaterialName: rawMaterial.name,
            quantityUsed: ingredient.quantity,
            unitCost,
            totalCost
          };
        }).filter((ing): ing is NonNullable<typeof ing> => ing !== null) || [];

        const totalCost = ingredients.reduce((sum, ing) => sum + (ing?.totalCost || 0), 0);
        const profit = revenue - totalCost;
        const profitMargin = revenue > 0 ? (profit / revenue) * 100 : 0;

        if (existing) {
          // Update existing calculation
          existing.quantitySold += orderItem.quantity;
          existing.totalRevenue += revenue;
          existing.totalCost += totalCost;
          existing.profit += profit;
          existing.profitMargin = existing.totalRevenue > 0 ? (existing.profit / existing.totalRevenue) * 100 : 0;
        } else {
          // Create new calculation
          profitMap.set(orderItem.menuItemId, {
            menuItemId: orderItem.menuItemId,
            menuItemName: orderItem.menuItemName,
            quantitySold: orderItem.quantity,
            totalRevenue: revenue,
            totalCost,
            profit,
            profitMargin,
            ingredients
          });
        }
      });
    });

    return Array.from(profitMap.values());
  };



  const tabs = [
    {
      id: 'orders' as const,
      label: 'Customer Orders',
      icon: ShoppingCart,
      count: reportData.summary.totalOrders
    },
    {
      id: 'transactions' as const,
      label: 'Transactions',
      icon: CreditCard,
      count: reportData.summary.totalTransactions
    },
    {
      id: 'stocks' as const,
      label: 'Stocks',
      icon: Package,
      count: reportData.summary.stockChanges
    },
    {
      id: 'profit' as const,
      label: 'Estimated Profit',
      icon: TrendingUp,
      count: reportData.profitCalculations.length
    }
  ];

  // Filtered data for each tab
  const filteredOrders = reportData.orders.filter(order => {
    const matchesSearch =
      order.customer?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.id?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus =
      orderStatusFilter === 'all' || order.status === orderStatusFilter;
    return matchesSearch && matchesStatus;
  });

  const filteredPayments = reportData.payments.filter(payment => {
    const matchesSearch =
      payment.customer?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      payment.orderId?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesMethod =
      paymentMethodFilter === 'all' || payment.method === paymentMethodFilter;
    return matchesSearch && matchesMethod;
  });

  const filteredStockChanges = reportData.stockChanges.filter(tx => {
    const matchesSearch =
      tx.itemName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tx.reason?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType =
      transactionTypeFilter === 'all' || tx.type === transactionTypeFilter;
    return matchesSearch && matchesType;
  });

  // Group stock transactions by item name and calculate totals
  const stockSummary = filteredStockChanges.reduce((acc, tx) => {
    const itemName = tx.itemName || 'Unknown Item';
    const unit = tx.unit || 'units';
    
    if (!acc[itemName]) {
      acc[itemName] = {
        itemName,
        unit,
        totalIn: 0,
        totalOut: 0,
        netChange: 0,
        transactionCount: 0
      };
    }
    
    if (tx.type === 'in') {
      acc[itemName].totalIn += tx.quantity || 0;
    } else if (tx.type === 'out') {
      acc[itemName].totalOut += tx.quantity || 0;
    }
    
    acc[itemName].netChange = acc[itemName].totalIn - acc[itemName].totalOut;
    acc[itemName].transactionCount += 1;
    
    return acc;
  }, {} as Record<string, {
    itemName: string;
    unit: string;
    totalIn: number;
    totalOut: number;
    netChange: number;
    transactionCount: number;
  }>);

  // Convert to array and sort by net change (most reduced first)
  const stockSummaryArray = Object.values(stockSummary).sort((a, b) => {
    // Show items with most stock out first, then by name
    if (a.totalOut !== b.totalOut) {
      return b.totalOut - a.totalOut;
    }
    return a.itemName.localeCompare(b.itemName);
  });

  // Filter profit calculations based on selected filter
  const filteredProfitCalculations = reportData.profitCalculations.filter(calc => {
    if (profitFilter === 'all') return true;
    if (profitFilter === 'rawMaterials') {
      // Only show items that have raw material ingredients
      return calc.ingredients && calc.ingredients.length > 0;
    }
    return true;
  });

  // Calculate filtered summary for profit cards
  const filteredProfitSummary = {
    totalRevenue: filteredProfitCalculations.reduce((sum, calc) => sum + calc.totalRevenue, 0),
    totalCost: filteredProfitCalculations.reduce((sum, calc) => sum + calc.totalCost, 0),
    totalProfit: filteredProfitCalculations.reduce((sum, calc) => sum + calc.profit, 0),
    averageProfitMargin: filteredProfitCalculations.length > 0 
      ? filteredProfitCalculations.reduce((sum, calc) => sum + calc.profitMargin, 0) / filteredProfitCalculations.length 
      : 0
  };

  // Payment summary by method
  const paymentSummary = ['cash', 'card', 'digital'].map(method => ({
    method,
    total: reportData.payments.filter(p => p.method === method).reduce((sum, p) => sum + (p.amount || 0), 0),
    count: reportData.payments.filter(p => p.method === method).length
  }));

  // Utility: Convert array of objects to CSV and trigger download
  function exportToCSV(filename: string, headers: string[], rows: (string | number)[][]) {
    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }

  // Export handlers for each tab
  const handleExport = () => {
    const { start, end } = getDateRangeForFilter(dateFilterType);
    const dateStr = dateFilterType === 'today' 
      ? new Date().toISOString().split('T')[0]
      : dateFilterType === 'range' || dateFilterType === 'custom'
      ? `${start.toISOString().split('T')[0]}_to_${end.toISOString().split('T')[0]}`
      : dateFilterType.replace('-', '_');
    
    if (activeTab === 'orders') {
      const headers = [
        'Order ID', 'Customer', 'Items', 'Total', 'Status', 'Time'
      ];
      const rows = filteredOrders.map(order => [
        order.id?.slice(-8) || '',
        order.customer || '',
        order.items?.length || 0,
        order.total?.toFixed(2) || '0.00',
        order.status || '',
        formatTime(order.createdAt)
      ]);
      exportToCSV(`orders_${dateStr}.csv`, headers, rows);
    } else if (activeTab === 'transactions') {
      const headers = [
        'Payment ID', 'Order ID', 'Customer', 'Amount', 'Method', 'Status', 'Time'
      ];
      const rows = filteredPayments.map(payment => [
        payment.id?.slice(-8) || '',
        payment.orderId?.slice(-8) || '',
        payment.customer || '',
        payment.amount?.toFixed(2) || '0.00',
        payment.method || '',
        payment.status || '',
        formatTime(payment.createdAt)
      ]);
      exportToCSV(`transactions_${dateStr}.csv`, headers, rows);
    } else if (activeTab === 'stocks') {
      if (stockViewMode === 'summary') {
        const headers = [
          'Item Name', 'Stock Out', 'Stock In', 'Net Change', 'Transactions'
        ];
        const rows = stockSummaryArray.map(item => [
          item.itemName,
          `${item.totalOut} ${item.unit}`,
          `${item.totalIn} ${item.unit}`,
          `${item.netChange >= 0 ? '+' : ''}${item.netChange} ${item.unit}`,
          item.transactionCount
        ]);
        exportToCSV(`stocks_summary_${dateStr}.csv`, headers, rows);
      } else {
        const headers = [
          'Item Name', 'Type', 'Quantity', 'Reason', 'Performed By', 'Source', 'Time'
        ];
        const rows = filteredStockChanges.map(tx => [
          tx.itemName || '',
          tx.type?.toUpperCase() || '',
          `${tx.quantity || 0} ${tx.unit || ''}`.trim(),
          tx.reason || '',
          tx.performedBy || '',
          tx.transactionSource || '',
          formatTime(tx.createdAt)
        ]);
        exportToCSV(`stocks_detailed_${dateStr}.csv`, headers, rows);
      }
    } else if (activeTab === 'profit') {
      const headers = [
        'Menu Item', 'Quantity Sold', 'Revenue', 'Cost', 'Profit', 'Profit Margin (%)'
      ];
      const rows = filteredProfitCalculations
        .sort((a, b) => b.profit - a.profit)
        .map(calc => [
          calc.menuItemName,
          calc.quantitySold,
          calc.totalRevenue.toFixed(2),
          calc.totalCost.toFixed(2),
          calc.profit.toFixed(2),
          calc.profitMargin.toFixed(1)
        ]);
      const filterSuffix = profitFilter === 'rawMaterials' ? '_raw_materials' : '_all';
      exportToCSV(`profit${filterSuffix}_${dateStr}.csv`, headers, rows);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading today's report...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-2 sm:p-4 md:p-6 lg:p-8">
      {/* Header */}
      <div className="mb-6 sm:mb-8 animate-fade-in">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-6 gap-2 sm:gap-0">
          <div className="animate-slide-in-left">
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-1 sm:mb-2">
              {getDateRangeForFilter(dateFilterType).label}
            </h1>
            <p className="text-gray-600 dark:text-gray-400 text-base sm:text-lg">
              Complete overview of business activities
            </p>
          </div>
          <div className="flex items-center space-x-2 sm:space-x-4 animate-slide-in-right">
            <button
              onClick={loadReportData}
              className="p-2 sm:p-3 bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-lg hover:scale-110 transition-all duration-500 ease-out"
              title="Refresh Data"
            >
              <RefreshCw className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 dark:text-gray-400" />
            </button>
            <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm rounded-lg px-3 py-1 sm:px-4 sm:py-2">
              Last updated: {lastUpdated.toLocaleTimeString()}
            </div>
          </div>
        </div>

        {/* Date Filter Section */}
        <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 sm:p-6 mb-6 animate-fade-in-up">
          <div className="flex flex-col space-y-4">
            {/* Desktop: Horizontal Button Group */}
            <div className="hidden md:flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Calendar className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Date Filter:</span>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => { setDateFilterType('today'); setShowDatePickers(false); }}
                    className={`px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
                      dateFilterType === 'today'
                        ? 'bg-primary-600 text-white shadow-md'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                  >
                    Today
                  </button>
                  <button
                    onClick={() => { setDateFilterType('this-week'); setShowDatePickers(false); }}
                    className={`px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
                      dateFilterType === 'this-week'
                        ? 'bg-primary-600 text-white shadow-md'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                  >
                    This Week
                  </button>
                  <button
                    onClick={() => { setDateFilterType('this-month'); setShowDatePickers(false); }}
                    className={`px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
                      dateFilterType === 'this-month'
                        ? 'bg-primary-600 text-white shadow-md'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                  >
                    This Month
                  </button>
                  <button
                    onClick={() => { setDateFilterType('all-time'); setShowDatePickers(false); }}
                    className={`px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
                      dateFilterType === 'all-time'
                        ? 'bg-primary-600 text-white shadow-md'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                  >
                    All Time
                  </button>
                  <button
                    onClick={() => { setDateFilterType('custom'); setShowDatePickers(true); }}
                    className={`px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
                      dateFilterType === 'custom'
                        ? 'bg-primary-600 text-white shadow-md'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                  >
                    Custom Date
                  </button>
                  <button
                    onClick={() => { setDateFilterType('range'); setShowDatePickers(true); }}
                    className={`px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
                      dateFilterType === 'range'
                        ? 'bg-primary-600 text-white shadow-md'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                  >
                    Date Range
                  </button>
                </div>
              </div>
            </div>

            {/* Mobile: Dropdown */}
            <div className="md:hidden">
              <div className="relative">
                <button
                  onClick={() => setShowDatePickers(!showDatePickers)}
                  className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                >
                  <div className="flex items-center space-x-2">
                    <Calendar className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                    <span className="font-medium">
                      {dateFilterType === 'today' && 'Today'}
                      {dateFilterType === 'this-week' && 'This Week'}
                      {dateFilterType === 'this-month' && 'This Month'}
                      {dateFilterType === 'all-time' && 'All Time'}
                      {dateFilterType === 'custom' && 'Custom Date'}
                      {dateFilterType === 'range' && 'Date Range'}
                    </span>
                  </div>
                  <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${showDatePickers ? 'rotate-180' : ''}`} />
                </button>
                
                {showDatePickers && (
                  <div className="absolute z-10 mt-2 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg overflow-hidden">
                    <button
                      onClick={() => { setDateFilterType('today'); setShowDatePickers(false); }}
                      className={`w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                        dateFilterType === 'today' ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 font-medium' : 'text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      Today
                    </button>
                    <button
                      onClick={() => { setDateFilterType('this-week'); setShowDatePickers(false); }}
                      className={`w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                        dateFilterType === 'this-week' ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 font-medium' : 'text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      This Week
                    </button>
                    <button
                      onClick={() => { setDateFilterType('this-month'); setShowDatePickers(false); }}
                      className={`w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                        dateFilterType === 'this-month' ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 font-medium' : 'text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      This Month
                    </button>
                    <button
                      onClick={() => { setDateFilterType('all-time'); setShowDatePickers(false); }}
                      className={`w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                        dateFilterType === 'all-time' ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 font-medium' : 'text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      All Time
                    </button>
                    <div className="border-t border-gray-200 dark:border-gray-700">
                      <button
                        onClick={() => setDateFilterType('custom')}
                        className={`w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                          dateFilterType === 'custom' ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 font-medium' : 'text-gray-700 dark:text-gray-300'
                        }`}
                      >
                        Custom Date
                      </button>
                      <button
                        onClick={() => setDateFilterType('range')}
                        className={`w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                          dateFilterType === 'range' ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 font-medium' : 'text-gray-700 dark:text-gray-300'
                        }`}
                      >
                        Date Range
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Date Pickers for Custom/Range */}
            {(dateFilterType === 'custom' || dateFilterType === 'range') && (
              <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                {dateFilterType === 'custom' ? (
                  <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-3">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Select Date:</label>
                    <input
                      type="date"
                      value={customDate}
                      onChange={(e) => setCustomDate(e.target.value)}
                      className="px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      max={new Date().toISOString().split('T')[0]}
                    />
                    <button
                      onClick={() => loadReportData()}
                      className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium text-sm"
                    >
                      Apply
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-3">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Date Range:</label>
                    <input
                      type="date"
                      value={rangeStartDate}
                      onChange={(e) => setRangeStartDate(e.target.value)}
                      className="px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      max={new Date().toISOString().split('T')[0]}
                    />
                    <span className="text-gray-500 dark:text-gray-400">to</span>
                    <input
                      type="date"
                      value={rangeEndDate}
                      onChange={(e) => setRangeEndDate(e.target.value)}
                      className="px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      max={new Date().toISOString().split('T')[0]}
                      min={rangeStartDate}
                    />
                    <button
                      onClick={() => loadReportData()}
                      className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium text-sm"
                    >
                      Apply
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6">
          <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-xl p-3 md:p-6 shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-lg hover:scale-105 transition-all duration-500 animate-fade-in-up">
            <div className="flex items-center justify-between mb-2 md:mb-4">
              <div className="w-8 h-8 md:w-12 md:h-12 bg-gradient-to-br from-blue-400 to-blue-600 rounded-lg md:rounded-xl flex items-center justify-center shadow-lg">
                <ShoppingCart className="w-4 h-4 md:w-6 md:h-6 text-white" />
              </div>
            </div>
            <p className="text-xs md:text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Total Orders</p>
            <p className="text-lg md:text-2xl font-bold text-gray-900 dark:text-white">{reportData.summary.totalOrders}</p>
          </div>

          <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-xl p-3 md:p-6 shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-lg hover:scale-105 transition-all duration-500 animate-fade-in-up animation-delay-100">
            <div className="flex items-center justify-between mb-2 md:mb-4">
              <div className="w-8 h-8 md:w-12 md:h-12 bg-gradient-to-br from-green-400 to-green-600 rounded-lg md:rounded-xl flex items-center justify-center shadow-lg">
                <DollarSign className="w-4 h-4 md:w-6 md:h-6 text-white" />
              </div>
            </div>
            <p className="text-xs md:text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Total Revenue</p>
            <p className="text-lg md:text-2xl font-bold text-gray-900 dark:text-white">₹{reportData.summary.totalRevenue.toFixed(0)}</p>
          </div>

          <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-xl p-3 md:p-6 shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-lg hover:scale-105 transition-all duration-500 animate-fade-in-up animation-delay-200">
            <div className="flex items-center justify-between mb-2 md:mb-4">
              <div className="w-8 h-8 md:w-12 md:h-12 bg-gradient-to-br from-purple-400 to-purple-600 rounded-lg md:rounded-xl flex items-center justify-center shadow-lg">
                <CreditCard className="w-4 h-4 md:w-6 md:h-6 text-white" />
              </div>
            </div>
            <p className="text-xs md:text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Transactions</p>
            <p className="text-lg md:text-2xl font-bold text-gray-900 dark:text-white">{reportData.summary.totalTransactions}</p>
          </div>

          <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-xl p-3 md:p-6 shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-lg hover:scale-105 transition-all duration-500 animate-fade-in-up animation-delay-300">
            <div className="flex items-center justify-between mb-2 md:mb-4">
              <div className="w-8 h-8 md:w-12 md:h-12 bg-gradient-to-br from-orange-400 to-orange-600 rounded-lg md:rounded-xl flex items-center justify-center shadow-lg">
                <Package className="w-4 h-4 md:w-6 md:h-6 text-white" />
              </div>
            </div>
            <p className="text-xs md:text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Stock Changes</p>
            <p className="text-lg md:text-2xl font-bold text-gray-900 dark:text-white">{reportData.summary.stockChanges}</p>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="flex overflow-x-auto scrollbar-hide px-6">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-4 border-b-2 font-medium text-sm flex items-center space-x-2 transition-colors whitespace-nowrap flex-shrink-0 ${
                    activeTab === tab.id
                      ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{tab.label}</span>
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    activeTab === tab.id
                      ? 'bg-primary-100 text-primary-800 dark:bg-primary-900/20 dark:text-primary-200'
                      : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                  }`}>
                    {tab.count}
                  </span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {/* Search and Filters */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 space-y-4 sm:space-y-0">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <div className="flex items-center space-x-2">
              {/* Tab-specific filters */}
              {activeTab === 'orders' && (
                <select
                  value={orderStatusFilter}
                  onChange={e => setOrderStatusFilter(e.target.value as any)}
                  className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="all">All Statuses</option>
                  <option value="active">Active</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              )}
              {activeTab === 'transactions' && (
                <select
                  value={paymentMethodFilter}
                  onChange={e => setPaymentMethodFilter(e.target.value as any)}
                  className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="all">All Methods</option>
                  <option value="cash">Cash</option>
                  <option value="card">Card</option>
                  <option value="digital">Digital</option>
                </select>
              )}
              {activeTab === 'stocks' && (
                <select
                  value={transactionTypeFilter}
                  onChange={e => setTransactionTypeFilter(e.target.value as any)}
                  className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="all">All Types</option>
                  <option value="in">Stock In</option>
                  <option value="out">Stock Out</option>
                </select>
              )}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center space-x-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                <Filter className="w-4 h-4" />
                <span>Filters</span>
              </button>
              <button onClick={handleExport} className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors">
                <Download className="w-4 h-4" />
                <span>Export</span>
              </button>
            </div>
          </div>

          {/* Orders Tab */}
          {activeTab === 'orders' && (
            <div className="space-y-4">
              {/* Desktop Table View */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50/50 dark:bg-gray-700/50">
                    <tr>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">Order ID</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">Customer</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">Items</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">Total</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">Status</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredOrders.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="text-center py-8 text-gray-500 dark:text-gray-400">No orders found.</td>
                      </tr>
                    ) : (
                      filteredOrders.map(order => (
                        <tr key={order.id} className="border-b border-gray-100 dark:border-gray-700">
                          <td className="py-3 px-4 font-medium text-gray-900 dark:text-white">{order.id?.slice(-8)}</td>
                          <td className="py-3 px-4 text-gray-700 dark:text-gray-300">{order.customer}</td>
                          <td className="py-3 px-4 text-gray-700 dark:text-gray-300">{order.items?.length || 0} items</td>
                          <td className="py-3 px-4 font-semibold text-gray-900 dark:text-white">₹{order.total?.toFixed(2)}</td>
                          <td className="py-3 px-4">
                            <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(order.status)}`}>{order.status || 'Unknown'}</span>
                          </td>
                          <td className="py-3 px-4 text-gray-600 dark:text-gray-400">{formatTime(order.createdAt)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              {/* Mobile Card View */}
              <div className="md:hidden space-y-4">
                {filteredOrders.length === 0 ? (
                  <div className="text-center py-12">
                    <ShoppingCart className="w-16 h-16 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
                    <h3 className="text-xl font-medium text-gray-900 dark:text-white mb-2">No orders today</h3>
                    <p className="text-gray-600 dark:text-gray-400">No customer orders have been placed today.</p>
                  </div>
                ) : (
                  filteredOrders.map(order => (
                    <div key={order.id} className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-3">
                          <h4 className="font-semibold text-gray-900 dark:text-white">Order #{order.id?.slice(-8)}</h4>
                          <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(order.status)}`}>{order.status || 'Unknown'}</span>
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">{formatTime(order.createdAt)}</div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                        <div>
                          <p className="text-gray-600 dark:text-gray-400">Customer</p>
                          <p className="font-medium text-gray-900 dark:text-white">{order.customer}</p>
                        </div>
                        <div>
                          <p className="text-gray-600 dark:text-gray-400">Items</p>
                          <p className="font-medium text-gray-900 dark:text-white">{order.items?.length || 0} items</p>
                        </div>
                        <div>
                          <p className="text-gray-600 dark:text-gray-400">Total</p>
                          <p className="font-medium text-gray-900 dark:text-white">₹{order.total?.toFixed(2)}</p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Transactions Tab */}
          {activeTab === 'transactions' && (
            <div className="space-y-4">
              {/* Payment Summary */}
              <div className="flex flex-wrap gap-4 mb-4">
                {paymentSummary.map(({ method, total, count }) => (
                  <div key={method} className="flex-1 min-w-[120px] bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 text-center">
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-1 capitalize">{method}</div>
                    <div className="text-lg font-bold text-gray-900 dark:text-white">₹{total.toFixed(2)}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">{count} txns</div>
                  </div>
                ))}
              </div>
              {/* Desktop Table View */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50/50 dark:bg-gray-700/50">
                    <tr>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">Payment ID</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">Order ID</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">Customer</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">Amount</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">Method</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">Status</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPayments.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="text-center py-8 text-gray-500 dark:text-gray-400">No transactions found.</td>
                      </tr>
                    ) : (
                      filteredPayments.map(payment => (
                        <tr key={payment.id} className="border-b border-gray-100 dark:border-gray-700">
                          <td className="py-3 px-4 font-medium text-gray-900 dark:text-white">{payment.id?.slice(-8)}</td>
                          <td className="py-3 px-4 text-gray-700 dark:text-gray-300">{payment.orderId?.slice(-8)}</td>
                          <td className="py-3 px-4 text-gray-700 dark:text-gray-300">{payment.customer}</td>
                          <td className="py-3 px-4 font-semibold text-gray-900 dark:text-white">₹{payment.amount?.toFixed(2)}</td>
                          <td className="py-3 px-4">
                            <span className={`px-3 py-1 rounded-full text-sm font-medium ${getPaymentMethodColor(payment.method)}`}>{payment.method}</span>
                          </td>
                          <td className="py-3 px-4">
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(payment.status)}`}>{payment.status || 'Unknown'}</span>
                          </td>
                          <td className="py-3 px-4 text-gray-600 dark:text-gray-400">{formatTime(payment.createdAt)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              {/* Mobile Card View */}
              <div className="md:hidden space-y-4">
                {filteredPayments.length === 0 ? (
                  <div className="text-center py-12">
                    <CreditCard className="w-16 h-16 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
                    <h3 className="text-xl font-medium text-gray-900 dark:text-white mb-2">No transactions today</h3>
                    <p className="text-gray-600 dark:text-gray-400">No payment transactions have been processed today.</p>
                  </div>
                ) : (
                  filteredPayments.map(payment => (
                    <div key={payment.id} className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-3">
                          <h4 className="font-semibold text-gray-900 dark:text-white">Payment #{payment.id?.slice(-8)}</h4>
                          <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getPaymentMethodColor(payment.method)}`}>{payment.method}</span>
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">{formatTime(payment.createdAt)}</div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                        <div>
                          <p className="text-gray-600 dark:text-gray-400">Customer</p>
                          <p className="font-medium text-gray-900 dark:text-white">{payment.customer}</p>
                        </div>
                        <div>
                          <p className="text-gray-600 dark:text-gray-400">Order ID</p>
                          <p className="font-medium text-gray-900 dark:text-white">#{payment.orderId?.slice(-8)}</p>
                        </div>
                        <div>
                          <p className="text-gray-600 dark:text-gray-400">Amount</p>
                          <p className="font-medium text-gray-900 dark:text-white">₹{payment.amount?.toFixed(2)}</p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Stocks Tab */}
          {activeTab === 'stocks' && (
            <div className="space-y-4">
              {/* Summary Message and View Toggle */}
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
                  <div className="flex items-center space-x-2">
                    <Package className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    <p className="text-blue-800 dark:text-blue-200 text-sm">
                      {stockViewMode === 'summary' 
                        ? 'Showing summary of total stock changes for each item today. Items are sorted by total stock out (most reduced first).'
                        : 'Showing detailed breakdown of all stock transactions today.'
                      }
                    </p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-blue-800 dark:text-blue-200 text-sm font-medium">View:</span>
                    <div className="flex bg-blue-100 dark:bg-blue-800 rounded-lg p-1">
                      <button
                        onClick={() => setStockViewMode('summary')}
                        className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                          stockViewMode === 'summary'
                            ? 'bg-white dark:bg-blue-600 text-blue-800 dark:text-blue-200 shadow-sm'
                            : 'text-blue-600 dark:text-blue-300 hover:text-blue-800 dark:hover:text-blue-200'
                        }`}
                      >
                        Summary
                      </button>
                      <button
                        onClick={() => setStockViewMode('detailed')}
                        className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                          stockViewMode === 'detailed'
                            ? 'bg-white dark:bg-blue-600 text-blue-800 dark:text-blue-200 shadow-sm'
                            : 'text-blue-600 dark:text-blue-300 hover:text-blue-800 dark:hover:text-blue-200'
                        }`}
                      >
                        Detailed
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              {/* Desktop Table View */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50/50 dark:bg-gray-700/50">
                    <tr>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">Item Name</th>
                      {stockViewMode === 'summary' ? (
                        <>
                          <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">Stock Out</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">Stock In</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">Net Change</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">Transactions</th>
                        </>
                      ) : (
                        <>
                          <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">Type</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">Quantity</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">Reason</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">Performed By</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">Source</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">Time</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {stockViewMode === 'summary' ? (
                      // Summary View
                      stockSummaryArray.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="text-center py-8 text-gray-500 dark:text-gray-400">No stock changes found.</td>
                        </tr>
                      ) : (
                        stockSummaryArray.map(item => (
                          <tr key={item.itemName} className="border-b border-gray-100 dark:border-gray-700">
                            <td className="py-3 px-4 font-medium text-gray-900 dark:text-white">{item.itemName}</td>
                            <td className="py-3 px-4">
                              <span className="px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-200">
                                {item.totalOut} {item.unit}
                              </span>
                            </td>
                            <td className="py-3 px-4">
                              <span className="px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-200">
                                {item.totalIn} {item.unit}
                              </span>
                            </td>
                            <td className="py-3 px-4">
                              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                                item.netChange >= 0 
                                  ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-200' 
                                  : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-200'
                              }`}>
                                {item.netChange >= 0 ? '+' : ''}{item.netChange} {item.unit}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-gray-700 dark:text-gray-300">{item.transactionCount} transactions</td>
                          </tr>
                        ))
                      )
                    ) : (
                      // Detailed View
                      filteredStockChanges.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="text-center py-8 text-gray-500 dark:text-gray-400">No stock changes found.</td>
                        </tr>
                      ) : (
                        filteredStockChanges.map(tx => (
                          <tr key={tx.id} className="border-b border-gray-100 dark:border-gray-700">
                            <td className="py-3 px-4 font-medium text-gray-900 dark:text-white">{tx.itemName}</td>
                            <td className="py-3 px-4">
                              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                                tx.type === 'in' 
                                  ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-200' 
                                  : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-200'
                              }`}>
                                {tx.type.toUpperCase()}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-gray-700 dark:text-gray-300">{tx.quantity} {tx.unit}</td>
                            <td className="py-3 px-4 text-gray-700 dark:text-gray-300">{tx.reason}</td>
                            <td className="py-3 px-4 text-gray-700 dark:text-gray-300">{tx.performedBy}</td>
                            <td className="py-3 px-4 text-gray-700 dark:text-gray-300">{tx.transactionSource}</td>
                            <td className="py-3 px-4 text-gray-600 dark:text-gray-400">{formatTime(tx.createdAt)}</td>
                          </tr>
                        ))
                      )
                    )}
                  </tbody>
                </table>
              </div>
              {/* Mobile Card View */}
              <div className="md:hidden space-y-4">
                {stockViewMode === 'summary' ? (
                  // Summary View Mobile
                  stockSummaryArray.length === 0 ? (
                    <div className="text-center py-12">
                      <Package className="w-16 h-16 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
                      <h3 className="text-xl font-medium text-gray-900 dark:text-white mb-2">No stock changes today</h3>
                      <p className="text-gray-600 dark:text-gray-400">No inventory transactions have been recorded today.</p>
                    </div>
                  ) : (
                    stockSummaryArray.map(item => (
                      <div key={item.itemName} className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-semibold text-gray-900 dark:text-white text-lg">{item.itemName}</h4>
                          <span className="text-sm text-gray-600 dark:text-gray-400">{item.transactionCount} transactions</span>
                        </div>
                        <div className="grid grid-cols-3 gap-4 text-sm">
                          <div className="text-center">
                            <p className="text-gray-600 dark:text-gray-400 mb-1">Stock Out</p>
                            <p className="font-semibold text-red-600 dark:text-red-400">{item.totalOut} {item.unit}</p>
                          </div>
                          <div className="text-center">
                            <p className="text-gray-600 dark:text-gray-400 mb-1">Stock In</p>
                            <p className="font-semibold text-green-600 dark:text-green-400">{item.totalIn} {item.unit}</p>
                          </div>
                          <div className="text-center">
                            <p className="text-gray-600 dark:text-gray-400 mb-1">Net Change</p>
                            <p className={`font-semibold ${
                              item.netChange >= 0 
                                ? 'text-green-600 dark:text-green-400' 
                                : 'text-red-600 dark:text-red-400'
                            }`}>
                              {item.netChange >= 0 ? '+' : ''}{item.netChange} {item.unit}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))
                  )
                ) : (
                  // Detailed View Mobile
                  filteredStockChanges.length === 0 ? (
                    <div className="text-center py-12">
                      <Package className="w-16 h-16 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
                      <h3 className="text-xl font-medium text-gray-900 dark:text-white mb-2">No stock changes today</h3>
                      <p className="text-gray-600 dark:text-gray-400">No inventory transactions have been recorded today.</p>
                    </div>
                  ) : (
                    filteredStockChanges.map(tx => (
                      <div key={tx.id} className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-3">
                            <h4 className="font-semibold text-gray-900 dark:text-white">{tx.itemName}</h4>
                            <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                              tx.type === 'in' 
                                ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-200' 
                                : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-200'
                            }`}>
                              {tx.type.toUpperCase()}
                            </span>
                          </div>
                          <div className="text-sm text-gray-600 dark:text-gray-400">{formatTime(tx.createdAt)}</div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <p className="text-gray-600 dark:text-gray-400">Quantity</p>
                            <p className="font-medium text-gray-900 dark:text-white">{tx.quantity} {tx.unit}</p>
                          </div>
                          <div>
                            <p className="text-gray-600 dark:text-gray-400">Reason</p>
                            <p className="font-medium text-gray-900 dark:text-white">{tx.reason}</p>
                          </div>
                          <div>
                            <p className="text-gray-600 dark:text-gray-400">Performed By</p>
                            <p className="font-medium text-gray-900 dark:text-white">{tx.performedBy}</p>
                          </div>
                          <div>
                            <p className="text-gray-600 dark:text-gray-400">Source</p>
                            <p className="font-medium text-gray-900 dark:text-white">{tx.transactionSource}</p>
                          </div>
                        </div>
                      </div>
                    ))
                  )
                )}
              </div>
            </div>
          )}

          {/* Profit Tab */}
          {activeTab === 'profit' && (
            <div className="space-y-4">
              {/* Profit Filter Header */}
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
                  <div className="flex items-center space-x-2">
                    <TrendingUp className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    <p className="text-blue-800 dark:text-blue-200 text-sm">
                      {profitFilter === 'all' 
                        ? 'Showing profit calculations for all menu items sold today.'
                        : 'Showing profit calculations for menu items containing raw materials only.'
                      }
                    </p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">Filter:</span>
                    <div className="flex bg-white dark:bg-blue-600 rounded-md p-1 shadow-sm">
                      <button
                        onClick={() => setProfitFilter('all')}
                        className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                          profitFilter === 'all'
                            ? 'bg-white dark:bg-blue-600 text-blue-800 dark:text-blue-200 shadow-sm'
                            : 'text-blue-600 dark:text-blue-300 hover:text-blue-800 dark:hover:text-blue-200'
                        }`}
                      >
                        All Items
                      </button>
                      <button
                        onClick={() => setProfitFilter('rawMaterials')}
                        className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                          profitFilter === 'rawMaterials'
                            ? 'bg-white dark:bg-blue-600 text-blue-800 dark:text-blue-200 shadow-sm'
                            : 'text-blue-600 dark:text-blue-300 hover:text-blue-800 dark:hover:text-blue-200'
                        }`}
                      >
                        Raw Materials Only
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Profit Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6">
                <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-xl p-3 md:p-6 shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-lg hover:scale-105 transition-all duration-500 animate-fade-in-up">
                  <div className="flex items-center justify-between mb-2 md:mb-4">
                    <div className="w-8 h-8 md:w-12 md:h-12 bg-gradient-to-br from-green-400 to-green-600 rounded-lg md:rounded-xl flex items-center justify-center shadow-lg">
                      <DollarSign className="w-4 h-4 md:w-6 md:h-6 text-white" />
                    </div>
                  </div>
                  <p className="text-xs md:text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Total Revenue</p>
                  <p className="text-lg md:text-2xl font-bold text-gray-900 dark:text-white">₹{filteredProfitSummary.totalRevenue.toFixed(0)}</p>
                </div>

                <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-xl p-3 md:p-6 shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-lg hover:scale-105 transition-all duration-500 animate-fade-in-up animation-delay-100">
                  <div className="flex items-center justify-between mb-2 md:mb-4">
                    <div className="w-8 h-8 md:w-12 md:h-12 bg-gradient-to-br from-red-400 to-red-600 rounded-lg md:rounded-xl flex items-center justify-center shadow-lg">
                      <Package className="w-4 h-4 md:w-6 md:h-6 text-white" />
                    </div>
                  </div>
                  <p className="text-xs md:text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Total Cost</p>
                  <p className="text-lg md:text-2xl font-bold text-gray-900 dark:text-white">₹{filteredProfitSummary.totalCost.toFixed(0)}</p>
                </div>

                <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-xl p-3 md:p-6 shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-lg hover:scale-105 transition-all duration-500 animate-fade-in-up animation-delay-200">
                  <div className="flex items-center justify-between mb-2 md:mb-4">
                    <div className="w-8 h-8 md:w-12 md:h-12 bg-gradient-to-br from-blue-400 to-blue-600 rounded-lg md:rounded-xl flex items-center justify-center shadow-lg">
                      <TrendingUp className="w-4 h-4 md:w-6 md:h-6 text-white" />
                    </div>
                  </div>
                  <p className="text-xs md:text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Total Profit</p>
                  <p className="text-lg md:text-2xl font-bold text-gray-900 dark:text-white">₹{filteredProfitSummary.totalProfit.toFixed(0)}</p>
                </div>

                <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-xl p-3 md:p-6 shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-lg hover:scale-105 transition-all duration-500 animate-fade-in-up animation-delay-300">
                  <div className="flex items-center justify-between mb-2 md:mb-4">
                    <div className="w-8 h-8 md:w-12 md:h-12 bg-gradient-to-br from-purple-400 to-purple-600 rounded-lg md:rounded-xl flex items-center justify-center shadow-lg">
                      <TrendingUp className="w-4 h-4 md:w-6 md:h-6 text-white" />
                    </div>
                  </div>
                  <p className="text-xs md:text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Profit Margin</p>
                  <p className="text-lg md:text-2xl font-bold text-gray-900 dark:text-white">{filteredProfitSummary.averageProfitMargin.toFixed(1)}%</p>
                </div>
              </div>

              {/* Desktop Table View */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50/50 dark:bg-gray-700/50">
                    <tr>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">Menu Item</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">Quantity Sold</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">Revenue</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">Cost</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">Profit</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700 dark:text-gray-300">Margin</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredProfitCalculations.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="text-center py-8 text-gray-500 dark:text-gray-400">
                          {profitFilter === 'rawMaterials' 
                            ? 'No menu items with raw materials found for today.' 
                            : 'No profit data available for today.'
                          }
                        </td>
                      </tr>
                    ) : (
                      filteredProfitCalculations
                        .sort((a, b) => b.profit - a.profit) // Sort by profit (highest first)
                        .map(calc => (
                          <tr key={calc.menuItemId} className="border-b border-gray-100 dark:border-gray-700">
                            <td className="py-3 px-4 font-medium text-gray-900 dark:text-white">{calc.menuItemName}</td>
                            <td className="py-3 px-4 text-gray-700 dark:text-gray-300">{calc.quantitySold}</td>
                            <td className="py-3 px-4 font-semibold text-gray-900 dark:text-white">₹{calc.totalRevenue.toFixed(2)}</td>
                            <td className="py-3 px-4 text-gray-700 dark:text-gray-300">₹{calc.totalCost.toFixed(2)}</td>
                            <td className="py-3 px-4">
                              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                                calc.profit >= 0 
                                  ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-200' 
                                  : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-200'
                              }`}>
                                ₹{calc.profit.toFixed(2)}
                              </span>
                            </td>
                            <td className="py-3 px-4">
                              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                                calc.profitMargin >= 0 
                                  ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-200' 
                                  : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-200'
                              }`}>
                                {calc.profitMargin.toFixed(1)}%
                              </span>
                            </td>
                          </tr>
                        ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="md:hidden space-y-4">
                {filteredProfitCalculations.length === 0 ? (
                  <div className="text-center py-12">
                    <TrendingUp className="w-16 h-16 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
                    <h3 className="text-xl font-medium text-gray-900 dark:text-white mb-2">
                      {profitFilter === 'rawMaterials' ? 'No raw material items' : 'No profit data today'}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      {profitFilter === 'rawMaterials' 
                        ? 'No menu items with raw materials were sold today.' 
                        : 'No menu items were sold today or profit calculations are not available.'
                      }
                    </p>
                  </div>
                ) : (
                  filteredProfitCalculations
                    .sort((a, b) => b.profit - a.profit) // Sort by profit (highest first)
                    .map(calc => (
                      <div key={calc.menuItemId} className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-semibold text-gray-900 dark:text-white text-lg">{calc.menuItemName}</h4>
                          <span className="text-sm text-gray-600 dark:text-gray-400">{calc.quantitySold} sold</span>
                        </div>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div className="text-center">
                            <p className="text-gray-600 dark:text-gray-400 mb-1">Revenue</p>
                            <p className="font-semibold text-green-600 dark:text-green-400">₹{calc.totalRevenue.toFixed(2)}</p>
                          </div>
                          <div className="text-center">
                            <p className="text-gray-600 dark:text-gray-400 mb-1">Cost</p>
                            <p className="font-semibold text-red-600 dark:text-red-400">₹{calc.totalCost.toFixed(2)}</p>
                          </div>
                          <div className="text-center">
                            <p className="text-gray-600 dark:text-gray-400 mb-1">Profit</p>
                            <p className={`font-semibold ${
                              calc.profit >= 0 
                                ? 'text-green-600 dark:text-green-400' 
                                : 'text-red-600 dark:text-red-400'
                            }`}>
                              ₹{calc.profit.toFixed(2)}
                            </p>
                          </div>
                          <div className="text-center">
                            <p className="text-gray-600 dark:text-gray-400 mb-1">Margin</p>
                            <p className={`font-semibold ${
                              calc.profitMargin >= 0 
                                ? 'text-green-600 dark:text-green-400' 
                                : 'text-red-600 dark:text-red-400'
                            }`}>
                              {calc.profitMargin.toFixed(1)}%
                            </p>
                          </div>
                        </div>
                      </div>
                    ))
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TodayReport; 